import { useState, useEffect } from 'react';
import { collection, onSnapshot, addDoc, deleteDoc, doc, serverTimestamp, query, orderBy } from 'firebase/firestore';
import { db } from '../../firebase';
import { uploadImageToBunnyDirect, deleteImageFromBunny } from '../../utils/bunnyUpload';
import { Loader2, Upload, Trash2, ImagePlus, CheckCircle2, AlertTriangle, X, Rocket } from 'lucide-react';

export default function AdminStudentWorks() {
  const [works, setWorks] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [feedback, setFeedback] = useState(null);

  // Form state
  const [studentName, setStudentName] = useState('');
  const [selectedCourseId, setSelectedCourseId] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  // Fetch existing works
  useEffect(() => {
    const q = query(collection(db, 'student_works'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(q, (snap) => {
      setWorks(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, () => setLoading(false));
    return unsub;
  }, []);

  // Fetch courses for dropdown
  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'courses'), (snap) => {
      setCourses(snap.docs.map(d => ({ id: d.id, title: d.data().title })));
    });
    return unsub;
  }, []);

  // Queue state for multi-upload
  const [pendingQueue, setPendingQueue] = useState([]);

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const MAX_SIZE = 2 * 1024 * 1024; // 2MB
    const tooLarge = files.filter(f => f.size > MAX_SIZE);
    const validFiles = files.filter(f => f.size <= MAX_SIZE);

    if (tooLarge.length > 0) {
      setFeedback({ type: 'error', msg: `تم تجاهل ${tooLarge.length} صور لأن حجمها يتعدى 2 ميجابايت` });
      setTimeout(() => setFeedback(null), 5000);
    }

    if (validFiles.length === 0) return;

    const newItems = validFiles.map(file => {
      const fileName = file.name.split('.').slice(0, -1).join('.');
      const cleanName = fileName.replace(/[_-]/g, ' ');
      return {
        file,
        preview: URL.createObjectURL(file),
        name: cleanName
      };
    });

    setPendingQueue(prev => [...prev, ...newItems]);

    // Automatically select the first one if nothing is currently selected
    if (!imageFile && newItems.length > 0) {
      selectFromQueue(newItems[0]);
    }
  };

  const selectFromQueue = (item) => {
    setImageFile(item.file);
    setImagePreview(item.preview);
    setStudentName(item.name);
    setSelectedCourseId(item.courseId || '');
  };

  const removeFromQueue = (fileToRemove, e) => {
    e?.stopPropagation();
    setPendingQueue(prev => {
      const filtered = prev.filter(item => item.file !== fileToRemove);
      
      // If we are removing the currently selected file, select the next one
      if (imageFile === fileToRemove) {
        if (filtered.length > 0) {
          // Use setTimeout to avoid state update during render/other state update if needed, 
          // but here we just want to select the first one of the REMAINING items.
          const nextItem = filtered[0];
          setImageFile(nextItem.file);
          setImagePreview(nextItem.preview);
          setStudentName(nextItem.name);
          setSelectedCourseId(nextItem.courseId || '');
        } else {
          setImageFile(null);
          setImagePreview(null);
          setStudentName('');
          setSelectedCourseId('');
        }
      }
      return filtered;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!studentName.trim() || !selectedCourseId || !imageFile) {
      setFeedback({ type: 'error', msg: 'يرجى ملء جميع الحقول' });
      setTimeout(() => setFeedback(null), 3000);
      return;
    }

    setUploading(true);
    setFeedback(null);

    try {
      const imageUrl = await uploadImageToBunnyDirect(imageFile, 'Student works');
      const selectedCourse = courses.find(c => c.id === selectedCourseId);

      await addDoc(collection(db, 'student_works'), {
        studentName: studentName.trim(),
        courseId: selectedCourseId,
        courseName: selectedCourse?.title || '',
        imageUrl,
        createdAt: serverTimestamp(),
      });

      // Remove the completed item from queue and move to next
      removeFromQueue(imageFile);
      setFeedback({ type: 'success', msg: 'تم إضافة العمل بنجاح!' });
    } catch (err) {
      console.error('Error adding student work:', err);
      setFeedback({ type: 'error', msg: err.message || 'حدث خطأ أثناء الرفع' });
    } finally {
      setUploading(false);
      setTimeout(() => setFeedback(null), 4000);
    }
  };

  const [bulkProgress, setBulkProgress] = useState(null);

  const handlePublishAll = async () => {
    if (pendingQueue.length === 0) return;
    if (!selectedCourseId) {
      setFeedback({ type: 'error', msg: 'يرجى اختيار الدورة أولاً ليتم تطبيقها على جميع الصور في القائمة' });
      return;
    }

    if (!window.confirm(`هل أنت متأكد من نشر جميع الأعمال (${pendingQueue.length}) لهذه الدورة؟`)) return;

    setUploading(true);
    setFeedback(null);
    const selectedCourse = courses.find(c => c.id === selectedCourseId);
    
    let successCount = 0;
    try {
      for (let i = 0; i < pendingQueue.length; i++) {
        const item = pendingQueue[i];
        setBulkProgress(`جاري رفع ${i + 1} من ${pendingQueue.length}...`);
        
        try {
          const imageUrl = await uploadImageToBunnyDirect(item.file, 'Student works');
          await addDoc(collection(db, 'student_works'), {
            studentName: item.name.trim(),
            courseId: selectedCourseId,
            courseName: selectedCourse?.title || '',
            imageUrl,
            createdAt: serverTimestamp(),
          });
          successCount++;
        } catch (err) {
          console.error(`Error uploading item ${i}:`, err);
        }
      }

      setPendingQueue([]);
      setImageFile(null);
      setImagePreview(null);
      setStudentName('');
      setFeedback({ type: 'success', msg: `تم بنجاح نشر ${successCount} عمل!` });
    } catch (err) {
      console.error('Bulk upload error:', err);
      setFeedback({ type: 'error', msg: 'حدث خطأ غير متوقع أثناء الرفع الجماعي' });
    } finally {
      setUploading(false);
      setBulkProgress(null);
      setTimeout(() => setFeedback(null), 5000);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا العمل؟')) return;
    try {
      const workToDelete = works.find(w => w.id === id);
      if (workToDelete?.imageUrl) {
        await deleteImageFromBunny(workToDelete.imageUrl);
      }
      await deleteDoc(doc(db, 'student_works', id));
    } catch (err) {
      console.error('Error deleting work:', err);
    }
  };

  const inputCls = 'w-full px-4 py-3 bg-white dark:bg-zinc-800 border border-slate-200 dark:border-white/10 rounded-xl outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 text-slate-900 dark:text-white font-medium transition-all';

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 space-y-8">
      <div>
        <h1 className="text-3xl font-extrabold text-slate-900 dark:text-white mb-2">أعمال مبدعي لباب</h1>
        <p className="text-lg text-slate-500 dark:text-zinc-400">إدارة أعمال الطلاب المتميزة لعرضها في الموقع.</p>
      </div>

      {/* Add New Work Form */}
      <form onSubmit={handleSubmit} className="bg-white dark:bg-zinc-900 border border-slate-200 dark:border-white/10 rounded-3xl p-8 shadow-sm space-y-6">
        <h2 className="text-xl font-black text-slate-900 dark:text-white flex items-center gap-2">
          <ImagePlus className="w-5 h-5 text-primary-500" />
          إضافة عمل جديد
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Student Name */}
          <div>
            <label className="block text-sm font-bold text-slate-700 dark:text-zinc-300 mb-2">اسم الطالب</label>
            <input
              type="text"
              value={studentName}
              onChange={(e) => {
                const val = e.target.value;
                setStudentName(val);
                // Sync back to queue to preserve edits when switching
                setPendingQueue(prev => prev.map(item =>
                  item.file === imageFile ? { ...item, name: val } : item
                ));
              }}
              placeholder="أدخل اسم الطالب"
              className={inputCls}
              required
            />
          </div>

          {/* Course Select */}
          <div>
            <label className="block text-sm font-bold text-slate-700 dark:text-zinc-300 mb-2">الدورة</label>
            <select
              value={selectedCourseId}
              onChange={(e) => {
                const val = e.target.value;
                setSelectedCourseId(val);
                // Sync back to queue
                setPendingQueue(prev => prev.map(item =>
                  item.file === imageFile ? { ...item, courseId: val } : item
                ));
              }}
              className={`${inputCls} cursor-pointer`}
              required
            >
              <option value="">اختر الدورة</option>
              {courses.map(c => (
                <option key={c.id} value={c.id}>{c.title}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Image Upload & Queue */}
        <div className="space-y-4">
          <label className="block text-sm font-bold text-slate-700 dark:text-zinc-300 mb-2">رفع الصور (يمكنك اختيار عدة صور)</label>
          <div className="flex items-start gap-4 flex-wrap">
            {/* Upload Button */}
            <label className="cursor-pointer flex flex-col items-center justify-center w-32 h-32 rounded-2xl border-2 border-dashed border-slate-300 dark:border-zinc-700 hover:border-primary-500 dark:hover:border-primary-500 bg-slate-50 dark:bg-zinc-800/50 transition-colors group shrink-0">
              <Upload className="w-6 h-6 text-slate-400 dark:text-zinc-500 group-hover:text-primary-500 transition-colors mb-1" />
              <span className="text-xs font-bold text-slate-500 dark:text-zinc-400 group-hover:text-primary-500 transition-colors">إضافة صور</span>
              <input type="file" accept="image/*" multiple onChange={handleImageChange} className="hidden" />
            </label>

            {/* Pending Queue */}
            {pendingQueue.map((item, idx) => (
              <div
                key={idx}
                onClick={() => selectFromQueue(item)}
                className={`group relative w-32 h-32 rounded-2xl overflow-hidden cursor-pointer border-2 transition-all ${
                  imageFile === item.file ? 'border-primary-500 ring-4 ring-primary-500/10' : 'border-transparent hover:border-slate-300 dark:hover:border-zinc-700'
                }`}
              >
                <img src={item.preview} alt="Queue" className="w-full h-full object-cover" />
                <button
                  onClick={(e) => removeFromQueue(item.file, e)}
                  className="absolute top-1 left-1 p-1 bg-red-500 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
                {imageFile === item.file && (
                  <div className="absolute inset-0 bg-primary-500/10 pointer-events-none flex items-center justify-center">
                    <div className="bg-primary-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">نشط</div>
                  </div>
                )}
              </div>
            ))}
          </div>
          {pendingQueue.length > 0 && (
            <p className="text-xs font-bold text-primary-600 dark:text-primary-400">
              وصل لديك {pendingQueue.length} صور في قائمة الانتظار. اضغط على أي صورة لتعديل بياناتها ثم اضغط "إضافة العمل".
            </p>
          )}
        </div>

        {/* Feedback */}
        {feedback && (
          <div className={`flex items-center gap-2 p-3 rounded-xl text-sm font-bold animate-in fade-in duration-300 ${
            feedback.type === 'success'
              ? 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/20'
              : 'bg-rose-50 dark:bg-rose-500/10 text-rose-600 dark:text-rose-400 border border-rose-200 dark:border-rose-500/20'
          }`}>
            {feedback.type === 'success' ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
            {feedback.msg}
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-wrap items-center gap-4">
          <button
            type="submit"
            disabled={uploading}
            className="px-8 py-3 bg-primary-600 hover:bg-primary-700 text-white font-bold rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {uploading && !bulkProgress ? <Loader2 className="w-5 h-5 animate-spin" /> : <ImagePlus className="w-5 h-5" />}
            {uploading && !bulkProgress ? 'جاري الرفع...' : 'إضافة العمل الحالي'}
          </button>

          {pendingQueue.length > 1 && (
            <button
              type="button"
              onClick={handlePublishAll}
              disabled={uploading}
              className="px-8 py-3 bg-slate-900 dark:bg-white dark:text-black text-white hover:bg-slate-800 dark:hover:bg-zinc-200 font-bold rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {bulkProgress ? <Loader2 className="w-5 h-5 animate-spin" /> : <Rocket className="w-5 h-5" />}
              {bulkProgress || `نشر الكل (${pendingQueue.length})`}
            </button>
          )}
        </div>
      </form>

      {/* Existing Works Grid */}
      <div>
        <h2 className="text-xl font-black text-slate-900 dark:text-white mb-4">الأعمال المضافة ({works.length})</h2>
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-10 h-10 animate-spin text-primary-500" />
          </div>
        ) : works.length === 0 ? (
          <div className="text-center py-16 text-slate-400 dark:text-zinc-600 font-bold text-lg">
            لا توجد أعمال بعد. أضف أول عمل أعلاه!
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {works.map(work => (
              <div key={work.id} className="group relative rounded-2xl overflow-hidden border border-slate-200 dark:border-white/10 bg-white dark:bg-zinc-900 shadow-sm hover:shadow-lg transition-all">
                <img src={work.imageUrl} alt={work.studentName} className="w-full aspect-square object-cover" />
                <div className="p-3 space-y-1">
                  <p className="font-bold text-sm text-slate-900 dark:text-white truncate">{work.studentName}</p>
                  <p className="text-xs text-slate-500 dark:text-zinc-400 truncate">{work.courseName}</p>
                </div>
                <button
                  onClick={() => handleDelete(work.id)}
                  className="absolute top-2 left-2 p-2 bg-red-500/90 text-white rounded-xl opacity-0 group-hover:opacity-100 transition-all hover:bg-red-600 shadow-lg"
                  title="حذف"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
